import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, Clock, ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

function RoleCard({ role, index }) {
  const navigate = useNavigate();

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      custom={index}
      className="border border-zinc-900 bg-black/40 p-6 sm:p-8 group hover:border-zinc-800 transition-colors duration-300"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="w-12 h-12 border border-zinc-800 flex items-center justify-center group-hover:border-[#BEF264]/30 transition-colors duration-300">
          <Briefcase className="w-5 h-5 text-zinc-500" />
        </div>
        <Badge
          className={`rounded-full text-[10px] font-mono uppercase tracking-wider px-3 py-1 border ${
            role.status === "open"
              ? "bg-[#BEF264]/10 text-[#BEF264] border-[#BEF264]/20"
              : "bg-zinc-800/50 text-zinc-500 border-zinc-800"
          }`}
        >
          {role.status === "open" ? "Open" : "Closed"}
        </Badge>
      </div>

      <h3 className="text-white text-xl sm:text-2xl font-bold tracking-tight mb-3">
        {role.title}
      </h3>
      <p className="text-zinc-400 text-sm leading-relaxed mb-6">
        {role.description}
      </p>

      <div className="border-t border-zinc-900 pt-5">
        <div
          dangerouslySetInnerHTML={{ __html: role.details }}
          className="text-zinc-500 text-xs leading-relaxed mb-6 [&_strong]:text-zinc-300 [&_strong]:font-medium [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mb-1"
        />

        {role.status === "open" ? (
          <Button
            onClick={() => navigate(`/apply/${role.id}`)}
            className="h-12 px-8 bg-white text-black hover:bg-[#BEF264] hover:text-black font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 group"
            data-testid={`apply-btn-${role.id}`}
          >
            Apply Now
            <ArrowRight className="w-4 h-4 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
        ) : (
          <Button
            disabled
            className="h-12 px-8 bg-transparent border border-zinc-800 text-zinc-600 font-mono text-xs uppercase tracking-[0.2em] cursor-not-allowed"
          >
            Applications Closed
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export default function CareersPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/roles`);
      setRoles(res.data);
    } catch (err) {
      toast.error("Failed to load job roles");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <img
            src="https://blanksage-public-assets.s3.ap-south-1.amazonaws.com/bstext.png"
            alt="Blanksage"
            className="h-6 sm:h-7 brightness-0 invert"
            data-testid="header-logo"
          />
          
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-40 pb-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={0}
          className="max-w-2xl"
        >
          <div className="w-10 h-[2px] bg-[#BEF264] mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-4">
            Join the<br />Team
          </h1>
          <p className="text-zinc-500 text-sm sm:text-base leading-relaxed max-w-sm font-light">
            We&apos;re looking for people who build things that matter. Explore open positions below.
          </p>
        </motion.div>
      </div>

      {/* Roles Grid */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-zinc-800 border-t-white rounded-full animate-spin" />
          </div>
        ) : roles.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-8 h-8 text-zinc-800 mx-auto mb-4" />
            <p className="text-zinc-600 text-sm">No open positions at the moment</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {roles.map((role, index) => (
              <RoleCard key={role.id} role={role} index={index} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-8 px-6 sm:px-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img
            src="https://blanksage-public-assets.s3.ap-south-1.amazonaws.com/bs+only+logo.png"
            alt="Blanksage"
            className="h-5 opacity-30 brightness-0 invert"
          />
          <p className="text-zinc-700 font-mono text-[10px] uppercase tracking-[0.15em]">
            Blanksage Careers
          </p>
        </div>
      </footer>
    </div>
  );
}